// File: components/ComponentRenderer.tsx
import { Box, Paper, Tooltip, Typography } from "@mui/material";
import TextComponent from "./TextComponent";
import parse from "html-react-parser";
import WidgetsIcon from "@mui/icons-material/Widgets";
import { Stack } from "@mui/system";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { DEVICE_CONFIG } from "../utils/constants";
import { WidgetContent } from "./WidgetRenderer";

// ---------------------------------------------------------------------------
// Component Renderer
// ---------------------------------------------------------------------------

const ComponentRenderer = ({
  component,
  selectedId,
  onSelect,
  onUpdate,
  theme,
  pageDictionary,
  setIsTextEditing,
  isTextEditing,
  setUnsavedText,
  siteBaseUrl,
}: any) => {
  const isSelected = selectedId === component?.id;

  switch (component.type) {
    case "text":
      return (
        <Box>
          <TextComponent
            component={component}
            isSelected={isSelected}
            onSelect={onSelect}
            onUpdate={onUpdate}
            theme={theme}
            pageDictionary={pageDictionary}
            setIsTextEditing={setIsTextEditing}
            isTextEditing={isTextEditing === component.id}
            setUnsavedText={setUnsavedText}
          />
        </Box>
      );

    case "widgets": {
      const widgetClass: string = component?.props?.className ?? "";
      const widgetHtml: string = component?.props?.text ?? "";
      const isDesktop = widgetClass.includes("internal-widget desktop");

      const deviceKey = Object.keys(DEVICE_CONFIG).find((key) =>
        widgetClass.includes(key),
      );
      const device = deviceKey ? DEVICE_CONFIG[deviceKey] : null;

      return (
        <Paper
          elevation={isSelected ? 4 : 1}
          className={widgetClass}
          id={`component-${component.id}`}
          data-device={widgetClass.split(" ").pop()}
          tabIndex={-1}
          onClick={() => onSelect(component.id)}
          sx={{
            cursor: "pointer",
            padding: 2,
            marginY: 1.5,
            marginBottom: "24px",
            border: isSelected ? "2px solid #9C27B080" : "1px solid #e0e0e0",
            borderRadius: 2,
            backgroundColor: component.props.backgroundColor || "#fff",
            transition: "box-shadow 0.2s ease-in-out",
            position: "relative",
            boxShadow: `
              0px 2px 1px -1px #9C27B033,
              0px 1px 1px 0px #9C27B024,
              0px 1px 3px 0px #9C27B01F
            `,
            "&:hover": { boxShadow: "0 2px 10px rgba(0,0,0,0.1)" },
          }}
        >
          {/* Card header row */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ color: "#000000DE" }}>
              <WidgetsIcon sx={{ width: 16, height: 16 }} />
              <Typography sx={{ fontWeight: 500 }}>Widget</Typography>
            </Stack>

            <Stack direction="row" alignItems="center" gap={1} sx={{ color: "#000000DE" }}>
              <Typography sx={{ fontWeight: 500 }}>Available On:</Typography>
              {device && (
                <Tooltip title={`Device: ${device.label}`}>
                  <device.Icon fontSize="small" />
                </Tooltip>
              )}
            </Stack>
          </Stack>

          {/* Widget content */}
          {isDesktop ? (
            <WidgetContent html={widgetHtml} siteBaseUrl={siteBaseUrl} />
          ) : (
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{
                fontSize: "14px",
                color: "#5F2120",
                backgroundColor: "#ffebee",
                borderRadius: "4px",
                padding: "6px 12px",
                fontWeight: 500,
                width: "100%",
              }}
            >
              <ErrorOutlineIcon fontSize="small" sx={{ color: "#D32F2F" }} />
              {parse(widgetHtml)}
            </Stack>
          )}

          <Typography variant="subtitle1" gutterBottom>
            {`${component?.props?.attributes?.widget_type} : ${component?.props?.attributes?.widget_name}`}
          </Typography>
        </Paper>
      );
    }

    default:
      return null;
  }
};

export default ComponentRenderer;
